/**
 * Phase 9.1 — EmployeeDetail page.
 *
 * Route: /employees/:canonical_id
 * Renders the full drill-down for one canonical employee. SKUs and orders
 * are clickable via the ProductDetailModal / OrderDetailModal handled by
 * the global DrillDownProvider (mounted in App.js).
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDrillDown } from "@/components/drilldown/DrillDownContext";
import { ClickableSKU, ClickableOrder } from "@/components/drilldown/Clickables";

const fmt = (n) => (n ?? 0).toLocaleString("es-MX");
const fmtDate = (iso) => (iso ? iso.slice(0, 10) : "—");
const fmtDatetime = (iso) =>
  iso ? `${iso.slice(0, 10)} ${iso.slice(11, 16)}` : "—";

export default function EmployeeDetail() {
  const { canonical_id } = useParams();
  const navigate = useNavigate();
  const { openProductModal, openOrderModal } = useDrillDown();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api
      .get(`/employees/${canonical_id}/drill-down`)
      .then(({ data }) => {
        if (!cancelled) setData(data);
      })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canonical_id]);

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-6" data-testid="employee-detail-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="h-7 gap-1 px-2"
          data-testid="employee-back-btn"
        >
          <ArrowLeft className="h-3 w-3" />
          Regresar
        </Button>
        <span>·</span>
        <Link to="/reports" className="hover:text-foreground">
          Reportes
        </Link>
        <span>›</span>
        <span className="text-foreground">
          {data?.canonical?.canonical_name || canonical_id}
        </span>
      </div>

      {loading && <p className="text-center text-muted-foreground">Cargando…</p>}
      {err && (
        <div className="rounded-md border border-red-800/40 bg-red-950/30 px-4 py-3 text-sm text-red-200">
          {err}
        </div>
      )}
      {!loading && data && (
        <>
          {/* Header card */}
          <Card className="p-5" data-testid="employee-header">
            <div className="flex items-baseline gap-3">
              <h1 className="text-2xl font-bold" data-testid="employee-name">
                {data.canonical.canonical_name}
              </h1>
              <span className="rounded-sm border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                {data.canonical.id.slice(-6)}
              </span>
              {data.kpis.top_source && (
                <Badge variant="secondary" className="uppercase">
                  {data.kpis.top_source}
                </Badge>
              )}
            </div>
            {data.canonical.aliases?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {data.canonical.aliases.map((a, i) => (
                  <Badge key={i} variant="outline" className="font-mono text-[10px]">
                    {a.code}
                    <span className="ml-1 opacity-60">· {a.source}</span>
                  </Badge>
                ))}
              </div>
            )}
          </Card>

          {/* KPI cards */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="employee-kpis">
            <Kpi label="Total transacciones" value={fmt(data.kpis.total_tx)} />
            <Kpi label="Tx/día promedio" value={fmt(data.kpis.tx_per_day_avg)} />
            <Kpi label="Días activo" value={fmt(data.kpis.unique_days_active)} />
            <Kpi label="Tipo dominante" value={data.kpis.top_tx_type || "—"} />
            <Kpi label="Fuente dominante" value={data.kpis.top_source || "—"} />
          </div>

          {/* Distribution by type + trend side-by-side on md+ */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-5" data-testid="employee-distribution">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Distribución por tipo
              </h2>
              {data.distribution_by_type.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sin datos.</p>
              ) : (
                <ul className="space-y-2">
                  {data.distribution_by_type.map((d) => (
                    <li key={d.type}>
                      <div className="mb-0.5 flex items-baseline justify-between text-sm">
                        <span className="font-mono">{d.type}</span>
                        <span className="tabular-nums text-muted-foreground">
                          {fmt(d.count)} · {d.pct}%
                        </span>
                      </div>
                      <div className="h-1.5 overflow-hidden rounded bg-muted">
                        <div
                          className="h-full bg-teal-400/80"
                          style={{ width: `${Math.min(100, d.pct)}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card className="p-5" data-testid="employee-trend">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Tendencia diaria (últimos {Math.min(30, data.trend_daily.length)} días con actividad)
              </h2>
              <MiniTrend series={data.trend_daily.slice(-30)} />
            </Card>
          </div>

          {/* Top SKUs + Top Orders */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-5" data-testid="employee-top-skus">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Top SKUs manejados
              </h2>
              {data.top_skus_handled.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sin datos.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="pb-2">SKU</th>
                      <th className="pb-2">Descripción</th>
                      <th className="pb-2 text-right">Tx</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {data.top_skus_handled.map((s) => (
                      <tr key={s.sku} className="hover:bg-muted/40">
                        <td className="py-1.5 font-mono">
                          <ClickableSKU sku={s.sku} onOpen={openProductModal}>
                            {s.sku}
                          </ClickableSKU>
                        </td>
                        <td className="max-w-[220px] truncate py-1.5 text-muted-foreground">
                          {s.description || "—"}
                        </td>
                        <td className="py-1.5 text-right tabular-nums">{fmt(s.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>

            <Card className="p-5" data-testid="employee-top-orders">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Top órdenes
              </h2>
              {data.top_orders.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sin datos.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="pb-2">Folio</th>
                      <th className="pb-2">Última actividad</th>
                      <th className="pb-2 text-right">Tx</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {data.top_orders.map((o) => (
                      <tr key={o.order_id} className="hover:bg-muted/40">
                        <td className="py-1.5 font-mono">
                          <ClickableOrder orderId={o.order_id} onOpen={openOrderModal}>
                            {o.order_id}
                          </ClickableOrder>
                        </td>
                        <td className="py-1.5 tabular-nums">{fmtDate(o.last_activity)}</td>
                        <td className="py-1.5 text-right tabular-nums">{fmt(o.tx_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>

          {/* Recent transactions */}
          <Card className="p-5" data-testid="employee-recent-tx">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Transacciones recientes (últimas {data.recent_transactions.length})
            </h2>
            {data.recent_transactions.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin datos.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="pb-2">Fecha</th>
                      <th className="pb-2">Tipo</th>
                      <th className="pb-2">SKU</th>
                      <th className="pb-2 text-right">Qty</th>
                      <th className="pb-2">Folio</th>
                      <th className="pb-2">Fuente</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {data.recent_transactions.map((t, i) => (
                      <tr key={i} className="hover:bg-muted/40">
                        <td className="py-1.5 tabular-nums text-xs">{fmtDatetime(t.timestamp)}</td>
                        <td className="py-1.5">
                          <Badge variant="secondary" className="font-mono text-[10px]">
                            {t.transaction_type}
                          </Badge>
                        </td>
                        <td className="py-1.5 font-mono">
                          <ClickableSKU sku={t.item} onOpen={openProductModal}>
                            {t.item}
                          </ClickableSKU>
                        </td>
                        <td className="py-1.5 text-right tabular-nums">{fmt(t.qty)}</td>
                        <td className="py-1.5">
                          {t.order_id ? (
                            <ClickableOrder orderId={t.order_id} onOpen={openOrderModal}>
                              {t.order_id}
                            </ClickableOrder>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="py-1.5 text-xs text-muted-foreground">
                          {t.source_report}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <Card className="border-border/60 bg-muted/40 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate text-lg font-semibold tabular-nums">{value}</div>
    </Card>
  );
}

// Micro sparkline — daily bars, no external chart lib.
function MiniTrend({ series }) {
  if (!series || series.length === 0) {
    return <p className="text-sm text-muted-foreground">Sin datos.</p>;
  }
  const max = Math.max(...series.map((d) => d.count), 1);
  return (
    <div className="flex h-24 items-end gap-1">
      {series.map((d) => (
        <div
          key={d.date}
          className="flex-1 rounded-t bg-teal-400/60 transition-all hover:bg-teal-300"
          style={{ height: `${(d.count / max) * 100}%` }}
          title={`${d.date}: ${d.count.toLocaleString()}`}
        />
      ))}
    </div>
  );
}
